import React, { ReactElement, ReactNode, forwardRef, ElementType } from "react"
import { Chip as MUIChip, Skeleton } from "@mui/material"
import { FaIcon } from "../../core/icons"
import IconButton from "../IconButton"
import { useStyles } from "./styles"

export type ChipDeleteIconPosition = "left" | "right"
export type ChipVariant = "filled" | "outlined" | "light"
export type ChipSize = "xs" | "sm" | "md" | "lg"
export type ChipColor =
  | "default"
  | "primary"
  | "secondary"
  | "success"
  | "warning"
  | "error"
  | "info"

export interface Props extends React.AriaAttributes {
  role?: React.AriaRole
  label?: ReactNode | ReactNode[]
  size?: ChipSize
  color?: ChipColor
  variant?: ChipVariant
  avatar?: ReactElement
  startIcon?: ReactElement
  deleteIconPosition?: ChipDeleteIconPosition
  endIcon?: ReactElement
  startChip?: ReactElement
  endChip?: ReactElement
  disable?: boolean
  loading?: boolean
  className?: string
  style?: React.CSSProperties
  component?: ElementType
  componentProps?: any
  onDelete?: React.EventHandler<any>
  onClick?: React.MouseEventHandler<HTMLDivElement>
  onMouseDown?: (event: any) => void
  "data-automation-id"?: string
}

const Chip = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    size = "md",
    color = "default",
    variant = "filled",
    disable = false,
    loading = false,
    deleteIconPosition = "right",
    label,
    avatar,
    startIcon,
    endIcon,
    startChip,
    endChip,
    className,
    component,
    componentProps = {},
    onDelete,
    onClick,
    onMouseDown,
    ["aria-label"]: ariaLabel,
    ["data-automation-id"]: dataAutomationId,
    ...rest
  } = props

  const iconOnly = !!startIcon && !label
  const deleteIcon =
    deleteIconPosition === "left" ? startIcon || startChip : endIcon || endChip
  const icon =
    deleteIconPosition === "right" ? startIcon || startChip : endIcon || endChip
  const clickable = !!(onClick || deleteIcon || onDelete)

  // a11y: the Chip owns the remove control as a real IconButton (focusable,
  // keyboard + screen-reader operable) so every removable chip has the same
  // remove button, named "Remove {chip name}" — the chip's aria-label, else its
  // text label. Skip it when an endChip (e.g. the breadcrumb dropdown) already
  // fills the slot — there onDelete only mounts the slot, it isn't a real remove.
  const removeTarget =
    ariaLabel ?? (typeof label === "string" ? label : undefined)
  const deleteButton =
    onDelete && !endChip ? (
      <IconButton
        size="xsmall"
        aria-label={removeTarget ? `Remove ${removeTarget}` : "Remove"}
      >
        <FaIcon solid name="times-circle" size="xsmall" />
      </IconButton>
    ) : (
      deleteIcon
    )

  // a11y: a delete-only chip is a labelled container, not a button. MUI would
  // otherwise make the whole chip a ButtonBase (role="button") wrapping the
  // remove button — a button nested in a button, so screen readers announce the
  // name twice and it's an extra tab stop. Expose it as a group and let the
  // visible label name it; the remove IconButton is the only control. Clickable,
  // link (component), and endChip chips stay interactive.
  const isRemovableOnly = !!onDelete && !onClick && !endChip && !component
  const rootAccessibilityProps = isRemovableOnly
    ? ({ role: "group", tabIndex: -1 } as const)
    : { "aria-label": ariaLabel }

  const { classes, cx } = useStyles({
    iconOnly,
    clickable,
    isBodyClickable: !!onClick || !!component,
    deleteIconPosition
  })

  if (loading) {
    return (
      <Skeleton
        component="div"
        variant="rectangular"
        className={cx(classes.skeleton, classes[size])}
      />
    )
  }

  return (
    <MUIChip
      component={component}
      ref={ref}
      label={iconOnly ? startIcon : label}
      avatar={avatar}
      icon={!iconOnly ? icon || startChip : undefined}
      deleteIcon={!iconOnly ? deleteButton || endChip : undefined}
      className={cx(
        classes.base,
        classes[size],
        classes[`${variant}_${color}`],
        className
      )}
      disabled={disable}
      onDelete={onDelete}
      onClick={onClick}
      onMouseDown={onMouseDown}
      {...rootAccessibilityProps}
      data-automation-id={dataAutomationId}
      {...componentProps}
      {...rest}
    />
  )
})

export default Chip
